import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faUsers,
  faChartLine,
  faLayerGroup,
  faCalendarAlt,
  faBox,
  faCog,
  faBolt,
  faUserShield,
  faTrash,
  faFileContract,
  faCopy,
  faShieldHalved,
  faEnvelope,
  faComments,
  faUserCheck,
  faGaugeHigh,
  faStore,
  faDesktop
} from '@fortawesome/free-solid-svg-icons';
import { useDashboardAuth } from '../../context/DashboardAuthContext';
import api from '../../services/api';
import usePlatform from '../../hooks/usePlatform';
import './Sidebar.css';

const Sidebar = ({ isOpen, onClose, badgeData }) => {
  const location = useLocation();
  const { hasPermission, isSuperAdmin } = useDashboardAuth();
  const { platform } = usePlatform();

  // Auto-close sidebar on route change (mobile only)
  React.useEffect(() => {
    if (window.innerWidth <= 768 && isOpen && onClose) {
      onClose();
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear badges when navigating to the relevant pages (with delay so user sees the badge)
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (location.pathname === '/users' && badgeData?.clearUsersBadge) {
        badgeData.clearUsersBadge();
      }
      if (location.pathname === '/groups' && badgeData?.clearGroupsBadge) {
        badgeData.clearGroupsBadge();
      }
    }, 2000); // 2s delay so user sees the count before clearing
    return () => clearTimeout(timer);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Shops registered from the desktop app that are waiting for approval (badge on "Inscriptions").
  // Polled rather than pushed: registrations are rare, and a minute's delay does not matter.
  const [pendingRegistrations, setPendingRegistrations] = React.useState(0);
  const canSeeRegistrations = hasPermission('approvals.view') || isSuperAdmin();

  React.useEffect(() => {
    if (!canSeeRegistrations) return undefined;

    let cancelled = false;
    const load = async () => {
      try {
        const response = await api.get('/dashboard-admin/registrations/count');
        if (!cancelled) setPendingRegistrations(response.data?.pending || 0);
      } catch (error) {
        // The badge is a convenience: an error just leaves it as it was.
      }
    };

    load();
    const timer = setInterval(load, 60000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [canSeeRegistrations, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lonnii Web (the React app). Remove this list and the switch once the web app is retired.
  const webItems = [
    { path: '/', icon: faHome, label: 'Tableau de Bord', permission: null }, // Always visible
    { path: '/temps-reel', icon: faBolt, label: 'Temps Réel', badge: 'Live', permission: 'realtime.view' },
    { path: '/users', icon: faUsers, label: 'Utilisateurs', permission: 'users.view' },
    { path: '/analytics', icon: faChartLine, label: 'Analytiques', permission: 'analytics.view' },
    { path: '/groups', icon: faLayerGroup, label: 'Espaces', permission: 'groups.view' },
    { path: '/activities', icon: faCalendarAlt, label: 'Activités', permission: 'activities.view' },
    { path: '/subscriptions', icon: faFileContract, label: 'Abonnements', permission: 'subscriptions.view' },
    { path: '/products', icon: faBox, label: 'Services', permission: 'products.view' },
    { path: '/stock-duplication', icon: faCopy, label: 'Dupliquer Stock', permission: null, superAdminOnly: true },
  ];

  // Lonnii Desktop (the WPF app), served by the .NET licence server.
  const desktopItems = [
    { path: '/desktop', icon: faGaugeHigh, label: "Vue d'ensemble", permission: 'desktop.view' },
    { path: '/desktop/shops', icon: faStore, label: 'Boutiques', permission: 'desktop.view' },
    { path: '/registrations', icon: faUserCheck, label: 'Inscriptions', permission: 'approvals.view' },
  ];

  // About the team, not one product: shown on both sides.
  const sharedItems = [
    { path: '/contact-messages', icon: faEnvelope, label: 'Messages', permission: 'contact.view' },
    { path: '/chat', icon: faComments, label: 'Chat', permission: 'chat.view' },
    { path: '/settings', icon: faCog, label: 'Paramètres', permission: 'settings.view' },
  ];

  const menuItems = [...(platform === 'desktop' ? desktopItems : webItems), ...sharedItems];

  // Home pages match exactly; others also stay lit on their sub-pages (a shop under Boutiques).
  const isActive = (path) =>
    path === '/' || path === '/desktop' ? location.pathname === path : location.pathname.startsWith(path);

  // Admin management - only visible to those with admins.view permission
  const adminMenuItem = { 
    path: '/admin-management', 
    icon: faUserShield, 
    label: 'Gestion Admins', 
    permission: 'admins.view' 
  };

  // Deletion requests / Approvals - visible to those with approvals.view permission
  const deletionRequestsItem = {
    path: '/deletion-requests',
    icon: faTrash,
    label: 'Approbations',
    permission: 'approvals.view'
  };

  // Filter menu items based on permissions
  const visibleMenuItems = menuItems.filter(item => {
    if (item.superAdminOnly) return isSuperAdmin();
    return item.permission === null || hasPermission(item.permission);
  });

  // Check if admin management should be visible
  const showAdminManagement = hasPermission('admins.view');
  // Deletion requests / Approvals concern web users and espaces, so they stay on the Web side.
  const showDeletionRequests = platform === 'web' && (hasPermission('approvals.view') || isSuperAdmin());

  // Get badge count for a menu item path
  const getBadgeCount = (path) => {
    if (path === '/registrations') return pendingRegistrations;
    if (!badgeData) return 0;
    if (path === '/users') return badgeData.newUsersCount || 0;
    if (path === '/groups') return badgeData.newGroupsCount || 0;
    if (path === '/contact-messages') return badgeData.newContactCount || 0;
    if (path === '/chat') return badgeData.newChatCount || 0;
    return 0;
  };

  return (
    <>
      {/* Mobile overlay backdrop */}
      {isOpen && (
        <div className="sidebar-overlay" onClick={onClose} />
      )}
      <div className={`sidebar ${isOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo-icon">
            <FontAwesomeIcon icon={platform === 'desktop' ? faDesktop : faShieldHalved} />
          </div>
          <div className="sidebar-brand">
            <span className="sidebar-brand-name">{platform === 'desktop' ? 'Lonnii Desktop' : 'Lonnii Admin'}</span>
            <span className="sidebar-brand-role">{platform === 'desktop' ? 'Gestion des boutiques' : 'Espace Admin'}</span>
          </div>
        </div>
      <nav className="sidebar-nav">
        {visibleMenuItems.map((item) => {
          const badgeCount = getBadgeCount(item.path);
          return (
          <Link
            key={item.path}
            to={item.path}
            className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
          >
            <FontAwesomeIcon icon={item.icon} className="nav-icon" />
            <span className="nav-label">{item.label}</span>
            {item.badge && <span className="nav-badge">{item.badge}</span>}
            {badgeCount > 0 && !item.badge && (
              <span className="nav-badge nav-badge-count">{badgeCount}</span>
            )}
          </Link>
          );
        })}

        {/* Separator before admin section */}
        {(showAdminManagement || showDeletionRequests) && (
          <>
            <div className="nav-separator"></div>
            {showDeletionRequests && (
              <Link
                to={deletionRequestsItem.path}
                className={`nav-item danger-nav ${location.pathname === deletionRequestsItem.path ? 'active' : ''}`}
              >
                <FontAwesomeIcon icon={deletionRequestsItem.icon} className="nav-icon" />
                <span className="nav-label">{deletionRequestsItem.label}</span>
              </Link>
            )}
            {showAdminManagement && (
              <Link
                to={adminMenuItem.path}
                className={`nav-item admin-nav ${location.pathname === adminMenuItem.path ? 'active' : ''}`}
              >
                <FontAwesomeIcon icon={adminMenuItem.icon} className="nav-icon" />
                <span className="nav-label">{adminMenuItem.label}</span>
              </Link>
            )}
          </>
        )}
      </nav>
    </div>
    </>
  );
};

export default Sidebar;
